import type { ReactNode } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { KitStepper } from "@/app/routes/dashboard/components/kit/Switch";
import { DEFAULT_WINDOW_WEEKS, isEvergreenKind, validUntilFromWeeks } from "./signalMeta";

export const MAX_WEEKS = 104;

/** The weeks box as a clamped number, or the kind default when it is empty. */
export function parseWeeks(weeksInput: string): number {
  const parsed = Number.parseInt(weeksInput, 10);
  return weeksInput === "" || Number.isNaN(parsed)
    ? DEFAULT_WINDOW_WEEKS
    : Math.min(MAX_WEEKS, Math.max(1, parsed));
}

/** What the two inputs resolve to: an ISO expiry, or null for "until superseded". */
export function resolveLifetimeChoice(weeksInput: string, openEnded: boolean): string | null {
  if (openEnded) return null;
  return validUntilFromWeeks(parseWeeks(weeksInput));
}

/** Only digits (or empty, mid-edit) reach the weeks box. */
export function acceptWeeksInput(raw: string): boolean {
  return raw === "" || /^\d+$/.test(raw);
}

/** On blur, an empty or zero box snaps to `fallback`; anything else is clamped to the cap. */
export function committedWeeksInput(weeksInput: string, fallback: string): string {
  const parsed = Number.parseInt(weeksInput, 10);
  if (weeksInput === "" || Number.isNaN(parsed) || parsed < 1) return fallback;
  return String(Math.min(MAX_WEEKS, parsed));
}

/**
 * Weeks + "until superseded" inputs shared by the create, edit and extend flows.
 * This is the ONLY way a validUntil is chosen on the page: 1 to 104 weeks, or
 * null "until superseded", which is only offered for the evergreen kinds
 * (positioning shift, audience shift). Renders no confirm/cancel actions of its
 * own; `hint` prints the resolved date beside the fields.
 */
export default function LifetimeFields({
  kind,
  weeksInput,
  openEnded,
  onWeeksChange,
  onOpenEndedChange,
  label = "Let this steer for",
  hint,
}: {
  kind: string;
  weeksInput: string;
  openEnded: boolean;
  onWeeksChange: (raw: string) => void;
  /** Kept for callers that tidied a typed box; the stepper never leaves one mid-edit. */
  onWeeksBlur?: () => void;
  onOpenEndedChange: (openEnded: boolean) => void;
  label?: string;
  hint?: ReactNode;
}) {
  const evergreenAllowed = isEvergreenKind(kind);
  const coarse = useMediaQuery("(pointer: coarse)");
  const weeks = parseWeeks(weeksInput);
  const unit = weeks === 1 ? "week" : "weeks";

  return (
    <div className="sig-lifetime">
      <span className="sig-field__label sig-field__label--mono">{label}</span>

      <div className="sig-lifetime__row">
        {/* The library's Stepper: one week a press, 1 to MAX_WEEKS. */}
        <div className="sig-lifetime__weeks" data-disabled={openEnded ? "true" : undefined}>
          <KitStepper
            value={weeks}
            label="Weeks"
            min={1}
            max={MAX_WEEKS}
            disabled={openEnded}
            large={coarse}
            onChange={(n) => onWeeksChange(String(n))}
          />
          <span className="sig-lifetime__unit">{unit}</span>
        </div>

        {evergreenAllowed ? (
          <label className="sig-lifetime__open">
            <input
              type="checkbox"
              checked={openEnded}
              onChange={(e) => onOpenEndedChange(e.target.checked)}
            />
            Until superseded
          </label>
        ) : null}

        {hint ? <span className="sig-lifetime__hint">{hint}</span> : null}
      </div>
    </div>
  );
}
